import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { X, AlertCircle, PartyPopper, CheckCircle, ExternalLink, Loader } from 'lucide-react';

import { eventTeamApiService } from '../../services/eventTeamApi';
import { cn } from '../../utils/cn';
import type {
  AdoptCardAsEventRequest,
  EventItem,
  EventItemStatus,
  EventKanbanLink,
} from '../../types/eventTeam';
import { EVENT_ITEM_STATUSES } from '../../types/eventTeam';

const PRIORITY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * Sync flags default ON for every new bridge — bridging two records is the explicit
 * signal that they should stay in lock-step. Finer control lives on the event detail
 * page's Workspaces panel (`<EventBridgeRow>`). Includes `sync_due_date` (event-specific).
 */
const ALL_SYNC_ON: Required<
  Pick<
    AdoptCardAsEventRequest,
    'sync_status' | 'sync_priority' | 'sync_due_date' | 'sync_assignees' | 'sync_comments' | 'propagate_watchers'
  >
> = {
  sync_status: true,
  sync_priority: true,
  sync_due_date: true,
  sync_assignees: true,
  sync_comments: true,
  propagate_watchers: true,
};

export interface AdoptAsEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  cardId: string;
  defaultTitle?: string;
  defaultDescription?: string;
  defaultPriority?: number;
  /** `createdEvent` is `false` when the backend reused an existing bridged event (idempotent). */
  onSuccess: (event: EventItem, link: EventKanbanLink, createdEvent: boolean) => void;
}

/**
 * Reverse direction of "Promote to card": creates an event from this kanban card and
 * bridges them (guide §1.5). Idempotent → 200 with `created_event:false`.
 */
export const AdoptAsEventModal = ({
  isOpen,
  onClose,
  cardId,
  defaultTitle,
  defaultDescription,
  defaultPriority,
  onSuccess,
}: AdoptAsEventModalProps) => {
  const navigate = useNavigate();

  const [title, setTitle] = useState<string>(defaultTitle ?? '');
  const [description, setDescription] = useState<string>(defaultDescription ?? '');
  const [status, setStatus] = useState<EventItemStatus>('idea');
  const [priority, setPriority] = useState<number>(defaultPriority ?? 5);
  const [copyAssignees, setCopyAssignees] = useState(true);
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ event: EventItem; createdEvent: boolean } | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSubmitting(false);
      setError(null);
      setSuccess(null);
      return;
    }
    setTitle(defaultTitle ?? '');
    setDescription(defaultDescription ?? '');
    setPriority(defaultPriority ?? 5);
    setStatus('idea');
    setCopyAssignees(true);
    setNotes('');
  }, [isOpen, defaultTitle, defaultDescription, defaultPriority]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const body: AdoptCardAsEventRequest = {
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
        status,
        priority,
        copy_assignees: copyAssignees,
        notes: notes.trim() || null,
        ...ALL_SYNC_ON,
      };
      const res = await eventTeamApiService.adoptCardAsEvent(cardId, body);
      setSuccess({ event: res.event, createdEvent: res.created_event });
      onSuccess(res.event, res.link, res.created_event);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to adopt card as event');
    } finally {
      setSubmitting(false);
    }
  };

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div className="modal-panel max-w-lg">
        <button
          type="button"
          onClick={onClose}
          className="modal-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="modal-header">
          <h2 className="modal-title flex items-center gap-2">
            <PartyPopper className="w-5 h-5 text-accent-400" />
            Adopt as event
          </h2>
        </div>
        {success ? (
          <div className="space-y-4">
            <div
              className={cn('alert', success.createdEvent ? 'alert-success' : 'alert-warning')}
              role="status"
            >
              <CheckCircle />
              <p className="text-sm font-medium">
                {success.createdEvent ? 'Event created.' : 'Card already bridged. Reusing the existing event.'}
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" onClick={onClose} className="glass-button-secondary">
                Close
              </button>
              <button
                onClick={() => {
                  onClose();
                  navigate(`/event-team/events/${encodeURIComponent(success.event.id)}`);
                }}
                className="btn-primary"
              >
                <ExternalLink className="w-4 h-4" />
                Open event
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div className="alert alert-danger" role="alert">
                <AlertCircle />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-glass-400 mb-1.5 block">Title (defaults to the card title)</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="glass-input w-full" />
            </div>

            <div>
              <label className="text-xs font-medium text-glass-400 mb-1.5 block">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="glass-input w-full"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-glass-400 mb-1.5 block">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as EventItemStatus)}
                  className="glass-input w-full"
                >
                  {EVENT_ITEM_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-glass-400 mb-1.5 block">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  className="glass-input w-full"
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      P{p}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-glass-300 cursor-pointer">
              <input
                type="checkbox"
                checked={copyAssignees}
                onChange={(e) => setCopyAssignees(e.target.checked)}
                className="form-checkbox"
              />
              Copy card assignees (roster members only)
            </label>

            <div>
              <label className="text-xs font-medium text-glass-400 mb-1.5 block">Bridge notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Optional context for this bridge…"
                className="glass-input w-full"
              />
            </div>

            <div className="modal-footer">
              <button type="button" onClick={onClose} className="glass-button-secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={submitting}
                className="btn-primary"
              >
                {submitting && <Loader className="w-4 h-4 animate-spin" />}
                Adopt as event
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
